import type { DataOption } from '@/common.types'
import type { MarkdownPeriodModel, ScenarioModel } from '@/shared/generated'
import type { ErrorSchema } from '@/shared/tools/json-schema-utils'
import type { JSONSchemaType } from 'ajv'

import React from 'react'
import { format } from 'date-fns'
import { find, pipe, prop, set, sortBy } from 'lodash/fp'
import { CalendarIcon, InfoIcon, PlusIcon, XIcon } from 'lucide-react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { usePricingCampaignContext } from '@/features/pricing-campaign/edit'

import { useSettingsSchemasQuery } from '@/entities/core'
import { useScenarioQuery } from '@/entities/scenario'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { Alert, AlertTitle } from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import {
	DatePicker,
	DatePickerCalendar,
	DatePickerContent,
	DatePickerInput,
} from '@/shared/components/ui/date-picker'
import { FormLabel } from '@/shared/components/ui/form'
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from '@/shared/components/ui/input-group'
import { Label } from '@/shared/components/ui/label'
import { LinkButton } from '@/shared/components/ui/link-button'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/ui/select'
import {
	Table,
	TableBody,
	TableCell,
	TableRow,
} from '@/shared/components/ui/table'
import { useAppStore } from '@/shared/contexts/app'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'
import { DateFormat, intlFormat, utcToZonedTime } from '@/shared/tools/dates'
import {
	asNumber,
	getEnumOptions,
	getResolvedSchema,
	translateOptions,
	translateSchemaKeys,
} from '@/shared/tools/json-schema-utils'

type MarkdownPeriodsProps = {
	schema?: JSONSchemaType<MarkdownPeriodModel>
	maxDate?: Date
	minDate?: Date
	defaultEndDate?: Date
	periods: MarkdownPeriodModel[]
	onChange(periods: MarkdownPeriodModel[]): void
	error?: ErrorSchema
}
export function MarkdownPeriodsField({
	schema,
	defaultEndDate,
	maxDate,
	minDate,
	periods = [],
	onChange: _onChange,
	error,
}: MarkdownPeriodsProps) {
	const { t } = useTranslation()
	const onChange = (data: MarkdownPeriodModel[]) =>
		_onChange(sortBy('start_date', [...data]))
	const handleChange = (index: number, name: string, value: string | number) =>
		onChange(
			periods.map((item, i) =>
				i === index ? { ...item, ...set(name, value, item) } : item
			)
		)
	const handleAddPeriod = () => {
		ampli.settingsPricingCampaignsMarkdownPeriodAddPeriod()
		const prev = periods[periods.length - 1]
		onChange([
			...periods,
			{
				start_date: format(
					prev?.start_date ? new Date(prev.start_date) : new Date(),
					DateFormat.system
				),
				target: {
					name: 'init_stock_share',
					value: 0,
				},
			},
		])
	}
	const handleRemoveStep = (index: number) => {
		ampli.settingsPricingCampaignsMarkdownPeriodDeletePeriod()
		onChange(periods.filter((item, i) => i !== index))
	}

	return (
		<>
			<div className='mb-2 flex flex-col space-y-2 border-b border-solid'>
				<Table>
					<TableBody className='border-t'>
						{periods.length > 0 ? (
							periods.map((item: MarkdownPeriodModel, index: number) => {
								const endDate = periods?.[index + 1]?.start_date
									? intlFormat(new Date(periods?.[index + 1]?.start_date))
									: maxDate
										? intlFormat(maxDate)
										: defaultEndDate
											? intlFormat(defaultEndDate)
											: '∞'

								return (
									<PeriodItem
										schema={schema}
										maxDate={maxDate}
										minDate={minDate}
										errorSchema={error?.[index]}
										key={`${item.start_date}_${endDate}`}
										endDate={endDate}
										onRemoveClick={handleRemoveStep}
										onChange={handleChange}
										item={item}
										index={index}
									/>
								)
							})
						) : (
							<TableRow>
								<TableCell className='border-none'>
									<div className='p-5 text-center text-sm text-muted-foreground'>
										{t('general_no_data', {
											defaultValue: 'No data',
										})}
									</div>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
			<Button
				variant='ghost'
				disabled={periods.length >= 15}
				onClick={handleAddPeriod}
			>
				<PlusIcon />
				{t('app.add_period', {
					defaultValue: 'Add period',
				})}
			</Button>
		</>
	)
}
type PeriodItemProps = {
	schema?: JSONSchemaType<MarkdownPeriodModel>
	maxDate?: Date
	minDate?: Date
	item: MarkdownPeriodModel
	index: number
	endDate: string
	onRemoveClick(index: number): void
	onChange(index: number, name: string, value?: string | number | null): void
	errorSchema?: ErrorSchema
}
function PeriodItem({
	schema,
	maxDate,
	minDate,
	item,
	index,
	endDate,
	onRemoveClick,
	onChange,
	errorSchema,
}: PeriodItemProps) {
	const { t } = useTranslation()
	const dateFormat = useAppStore((state) => state.dateFormat)
	const availableConstraints = schema?.items?.properties?.target?.oneOf ?? []
	const paramsValue = item.target?.value
	const startDate = item.start_date

	const handleRemoveClick = () => onRemoveClick(index)
	const handleStartDateChange = (date: Date) =>
		onChange(index, 'start_date', format(date, DateFormat.system))
	const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) =>
		onChange(index, 'target.value', asNumber(event.target.value))
	const handleStartegyChange = (value: string) =>
		onChange(index, 'target.name', value)

	const strategiesOptions = availableConstraints.map(
		(schemaChoice: JSONSchemaType<MarkdownPeriodModel>) => ({
			label: t(schemaChoice.title, {
				defaultValue: schemaChoice.title,
			}),
			value: schemaChoice.properties.name.const,
		})
	) as DataOption[]

	const measure = availableConstraints.find(
		({ properties: { name } }: JSONSchemaType<MarkdownPeriodModel>) =>
			item.target.name === name.const
	)?.properties?.value?.measure

	return (
		<TableRow>
			<TableCell className='border-none p-2'>
				<div className='flex gap-2'>
					<div className='mb-0'>
						<DatePicker
							required
							value={
								startDate
									? utcToZonedTime(new Date(startDate), 'UTC')
									: undefined
							}
							onValueChange={handleStartDateChange}
							mode='single'
						>
							<DatePickerInput
								format={dateFormat}
								maxDate={minDate}
								minDate={minDate}
							/>
							<DatePickerContent>
								<DatePickerCalendar
									disabled={{
										before: minDate!,
										after: maxDate!,
									}}
								/>
							</DatePickerContent>
						</DatePicker>
						{errorSchema?.start_date && (
							<p className='text-sm text-destructive'>
								{errorSchema?.start_date?.message}
							</p>
						)}
					</div>
					<span className='inline-flex h-8 items-center'>-</span>
					<div className='mb-0'>
						<InputGroup>
							<InputGroupInput name='end_date' value={endDate} disabled />
							<InputGroupAddon align='inline-end'>
								<CalendarIcon className='size-4 cursor-pointer' />
							</InputGroupAddon>
						</InputGroup>
					</div>

					<div className='mr-2.5 mb-0 w-64'>
						<Select
							value={item.target.name}
							onValueChange={handleStartegyChange}
						>
							<SelectTrigger
								name='target.name'
								data-name='target.name'
								aria-invalid={errorSchema?.target}
							>
								<SelectValue placeholder='Select strategy' />
							</SelectTrigger>
							<SelectContent>
								{strategiesOptions.map((option) => (
									<SelectItem
										key={option.value}
										value={option.value}
										data-value={option.value}
									>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						{errorSchema?.target && (
							<p className='text-sm text-destructive'>
								{t('validation.required_filed', {
									defaultValue: 'This field is required.',
								})}
							</p>
						)}
					</div>
					<div className='mb-0 w-32 shrink-0'>
						<InputGroup>
							<InputGroupInput
								name='target.value'
								type='number'
								value={paramsValue}
								aria-invalid={
									(!paramsValue && paramsValue != 0) || errorSchema?.target
								}
								onChange={handleValueChange}
							/>
							<InputGroupAddon align='inline-end'>{measure}</InputGroupAddon>
						</InputGroup>
						{errorSchema?.target?.value && (
							<p className='text-sm text-destructive'>
								{errorSchema?.target?.value?.message}
							</p>
						)}
					</div>
					<Button
						data-testid='remove-markdown-period-button'
						variant='ghost'
						className='ml-auto'
						onClick={handleRemoveClick}
						size='icon'
					>
						<XIcon />
					</Button>
				</div>
			</TableCell>
		</TableRow>
	)
}

export function PricingTacticMarkdowFields() {
	const { t } = useTranslation()
	const { showModal } = useModals()
	const { scenarioId, isScenarioContext, optimizationGroupId, isMainGlobal } =
		usePricingCampaignContext()
	const settingsStrategy = useScenarioQuery<
		ScenarioModel['settings']['strategy']
	>(scenarioId!, {
		select: prop('settings.strategy'),
		refetchOnWindowFocus: 'always',
		enabled: !!scenarioId,
	})
	const schema = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('part:strategy'),
			prop('properties.markdown_strategy'),
			(schema) => translateSchemaKeys(schema, t),
		]),
		refetchOnWindowFocus: 'always',
	})

	const targetOptions = translateOptions(
		getEnumOptions(prop('properties.target', schema.data)),
		t
	)
	const markdownStrategyLabel =
		find(
			{ value: settingsStrategy.data?.markdown_strategy?.target },
			targetOptions
		)?.label || '-'
	const label = markdownStrategyLabel
	if (isMainGlobal) {
		return (
			<Alert variant='info'>
				<AlertTitle>
					{t('global.pc.settings.demand_strategy.message', {
						defaultValue:
							'Pricing strategy will be inherited from the Optimization group settings.',
					})}
				</AlertTitle>
			</Alert>
		)
	}

	return (
		<>
			<div className='flex items-center gap-2'>
				<FormLabel className='inline-flex w-auto text-sm text-muted-foreground'>
					{t('pc.settings.markdown.strategy', {
						defaultValue: 'Markdown strategy',
					})}
				</FormLabel>
				<span className='text-sm'>
					{t('app.grow_metric', {
						label,
						defaultValue: `Grow ${label}`,
					})}
				</span>
				{!isScenarioContext && (
					<LinkButton
						variant='brand'
						onClick={() => {
							showModal('OPTIMIZATION_GROUP_MODAL', {
								optimizationGroupId: optimizationGroupId!,
							})
							ampli.settingsPricingCampaignsMarkdownChangeClick()
						}}
					>
						{t('app.change', {
							defaultValue: 'Change',
						})}
					</LinkButton>
				)}
			</div>

			{settingsStrategy.data?.markdown_strategy?.target === 'revenue' ? (
				<Alert variant='warning' className='mt-2'>
					<InfoIcon />
					<AlertTitle>
						{t('pc.settings.markdown.strategy.revenue.warning', {
							defaultValue:
								'Markdown goals aren`t available for Revenue strategy',
						})}
					</AlertTitle>
				</Alert>
			) : null}
		</>
	)
}
export function MarkdownPeriodsFieldController({ name }: { name: string }) {
	const { t } = useTranslation()
	const { control, watch } = useFormContext()
	const endDate = watch('settings.activity.end_date')
	const periodsSchema = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('part:tactics_md'),
			prop('properties.params.properties.periods'),
		]),
		refetchOnWindowFocus: 'always',
	})

	return (
		<Controller
			name={`${name}.periods`}
			control={control}
			render={({
				field: { value, onChange },
				fieldState: { error },
				formState: { errors },
			}) => (
				<ErrorBoundary>
					<Label className='mt-4 mb-1.5'>
						{t('app.markdown_periods.title', {
							defaultValue: 'Markdown periods',
						})}
					</Label>
					<MarkdownPeriodsField
						schema={periodsSchema.data}
						defaultEndDate={endDate ? new Date(endDate) : undefined}
						periods={value || []}
						error={error}
						onChange={onChange}
					/>
					{(errors?.settings as {
						pricing_tactics?: { message?: string }
					}) && (
						<p className='text-sm text-destructive'>
							{
								(
									errors.settings as {
										pricing_tactics?: { message?: string }
									}
								).pricing_tactics?.message
							}
						</p>
					)}
				</ErrorBoundary>
			)}
		/>
	)
}
